import { AriaAttributes, Children, ElementType, FC, MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';
import { Body } from '@components/text';
import './CarouselAction.scss';

export interface CarouselActionProps extends AriaAttributes {
  /** Text label for the default action link (e.g. "Edit"). Ignored when `children` is provided. */
  label?: string;
  /** Destination URL. With `href` the label renders as a link; without it, the label renders as a focusable, non-navigating `<button>`. Pair with `linkComponent` to render the link as a custom router link. */
  href?: string;
  /** Custom component to render the link instead of a native `<a>` (e.g. Next.js Link). Must accept `href`. Ignored when no `href` is provided (renders a `<button>`). */
  linkComponent?: ElementType;
  /** Callback fired when the action (link or button) is clicked. */
  onClick?: (event: MouseEvent) => void;
  /** Custom content rendered in place of the default label link (e.g. a `<button>`). The consumer owns its accessibility — place any `aria-*` on your own control (the component's `aria-*` props apply only to the default label link). */
  children?: ReactNode;
  /** Additional class name. */
  className?: string;
}

/**
 * Carousel action component
 *
 * Renders an optional action (e.g. an "Edit" link) in the top-right of a carousel
 * title row. Provide `label` (+ optional `href`/`linkComponent`/`onClick`) for the default
 * control — a link when `href` is set, otherwise a focusable non-navigating `<button>` — or
 * pass `children` to render a fully custom element. Renders nothing when neither `children`
 * nor `label` is provided.
 *
 * @param label - Text label for the default action (e.g. "Edit"). Ignored when `children` is provided.
 * @param href - Destination URL. With `href` the label renders as a link; without it, as a focusable non-navigating `<button>`. Pair with `linkComponent` to render the link as a custom router link.
 * @param linkComponent - Custom component to render the link instead of a native `<a>` (e.g. Next.js Link). Must accept `href`. Ignored when no `href` is provided.
 * @param onClick - Callback fired when the action (link or button) is clicked.
 * @param children - Custom content rendered in place of the default label link (e.g. a `<button>`). The consumer owns its accessibility — place any `aria-*` on your own control (the component's `aria-*` props apply only to the default label link).
 * @param className - Additional class name.
 */
export const CarouselAction: FC<CarouselActionProps> = ({
  label,
  href,
  linkComponent,
  onClick,
  children,
  className,
  ...rest
}) => {
  // Render children wrapper only if content is valid (non-empty, non-boolean).
  // Excludes `aria-*` since a custom child manages its own accessibility.
  if (Children.count(children) > 0 && typeof children !== 'boolean') {
    return <div className={classNames('vds-carousel-action', className)}>{children}</div>;
  }

  // No label → nothing to render.
  if (!label) {
    return null;
  }

  // Body typography per design; link with href, else a non-navigating button.
  const content = (
    <Body variant="body-01" size="small" color="primary">
      {label}
    </Body>
  );
  const LinkWrapper = linkComponent || 'a';
  return (
    <div className={classNames('vds-carousel-action', className)}>
      {href ? (
        <LinkWrapper href={href} onClick={onClick} className="vds-carousel-action__link" {...rest}>
          {content}
        </LinkWrapper>
      ) : (
        <button type="button" onClick={onClick} className="vds-carousel-action__link" {...rest}>
          {content}
        </button>
      )}
    </div>
  );
};
